/**
 * Rótulos localizados de categorias de foco — módulo puro e partilhado.
 *
 * Usado pelo motor de rotas (`ai-stops.functions.ts`) e pelo modo Descoberta
 * (`discovery-intent.ts`). Fica isolado para evitar acoplamento ou
 * dependências circulares entre os dois fluxos. Sem imports, sem estado.
 */

export type CategoryFocusId =
  | "cafe"
  | "restaurant"
  | "hotel"
  | "viewpoint"
  | "museum"
  | "monument"
  | "church"
  | "park"
  | "nature"
  | "water"
  | "shopping"
  | "nightlife"
  | "market"
  | "sweets"
  | "bar"
  | "beach"
  | "nature_park"
  | "water_park"
  | "amusement_park"
  | "attraction"
  | "breakfast";

export type LocalizedLabel = {
  pt: string;
  en: string;
  es: string;
  fr: string;
  it: string;
  de: string;
};

/** Rótulo em minúsculas, para frases ("melhores <rótulo> em …"). */
export const CATEGORY_FOCUS_LABELS: Record<CategoryFocusId, LocalizedLabel> = {
  cafe: { pt: "cafés e pastelarias", en: "cafés and bakeries", es: "cafeterías", fr: "cafés", it: "caffè", de: "Cafés" },
  restaurant: { pt: "restaurantes", en: "restaurants", es: "restaurantes", fr: "restaurants", it: "ristoranti", de: "Restaurants" },
  hotel: { pt: "hotéis", en: "hotels", es: "hoteles", fr: "hôtels", it: "hotel", de: "Hotels" },
  viewpoint: { pt: "miradouros", en: "viewpoints", es: "miradores", fr: "points de vue", it: "punti panoramici", de: "Aussichtspunkte" },
  museum: { pt: "museus", en: "museums", es: "museos", fr: "musées", it: "musei", de: "Museen" },
  monument: { pt: "monumentos", en: "monuments", es: "monumentos", fr: "monuments", it: "monumenti", de: "Denkmäler" },
  church: { pt: "igrejas", en: "churches", es: "iglesias", fr: "églises", it: "chiese", de: "Kirchen" },
  park: { pt: "parques e jardins", en: "parks and gardens", es: "parques y jardines", fr: "parcs et jardins", it: "parchi e giardini", de: "Parks und Gärten" },
  nature: { pt: "natureza", en: "nature", es: "naturaleza", fr: "nature", it: "natura", de: "Natur" },
  water: { pt: "rios, lagos e praias", en: "rivers, lakes and beaches", es: "ríos, lagos y playas", fr: "rivières, lacs et plages", it: "fiumi, laghi e spiagge", de: "Flüsse, Seen und Strände" },
  shopping: { pt: "compras", en: "shopping", es: "compras", fr: "shopping", it: "shopping", de: "Shopping" },
  nightlife: { pt: "vida noturna", en: "nightlife", es: "vida nocturna", fr: "vie nocturne", it: "vita notturna", de: "Nachtleben" },
  market: { pt: "mercados", en: "markets", es: "mercados", fr: "marchés", it: "mercati", de: "Märkte" },
  sweets: { pt: "doçaria tradicional", en: "traditional sweets", es: "dulces tradicionales", fr: "pâtisseries traditionnelles", it: "dolci tradizionali", de: "traditionelle Süßigkeiten" },
  bar: { pt: "bares", en: "bars", es: "bares", fr: "bars", it: "bar", de: "Bars" },
  beach: { pt: "praias", en: "beaches", es: "playas", fr: "plages", it: "spiagge", de: "Strände" },
  nature_park: { pt: "parques naturais", en: "nature parks", es: "parques naturales", fr: "parcs naturels", it: "parchi naturali", de: "Naturparks" },
  water_park: { pt: "parques aquáticos", en: "water parks", es: "parques acuáticos", fr: "parcs aquatiques", it: "parchi acquatici", de: "Wasserparks" },
  amusement_park: { pt: "parques temáticos", en: "theme parks", es: "parques temáticos", fr: "parcs à thème", it: "parchi a tema", de: "Freizeitparks" },
  attraction: { pt: "atrações", en: "attractions", es: "atracciones", fr: "attractions", it: "attrazioni", de: "Attraktionen" },
  breakfast: { pt: "pequenos-almoços", en: "breakfast spots", es: "desayunos", fr: "petits-déjeuners", it: "colazioni", de: "Frühstück" },
};

const langKey = (language = "pt"): keyof LocalizedLabel => {
  const l = (language || "pt").toLowerCase().slice(0, 2);
  return (["pt", "en", "es", "fr", "it", "de"] as const).includes(l as keyof LocalizedLabel)
    ? (l as keyof LocalizedLabel)
    : "en";
};

/** Rótulo em minúsculas na língua pedida (fallback inglês). */
export function categoryFocusLabel(id: CategoryFocusId, language = "pt"): string {
  return CATEGORY_FOCUS_LABELS[id][langKey(language)];
}

/** Rótulo com inicial maiúscula, para o cartão de paragem ("Doçaria tradicional"). */
export function categoryFocusDisplayLabel(id: CategoryFocusId, language = "pt"): string {
  const label = categoryFocusLabel(id, language);
  return label.charAt(0).toUpperCase() + label.slice(1);
}
